import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { resources } from "@/i18n/resources";
import { ApiError } from "./api";

export type ErrorKey = keyof (typeof resources)["en"]["errors"];

/**
 * Returns a translator for error codes (API error codes and field error codes
 * share the `errors` namespace). Unknown codes fall back to a generic message.
 */
export function useErrorText() {
  const { t, i18n } = useTranslation("errors");
  return (code: string | undefined, fallback: ErrorKey = "unknown"): string =>
    t(code && i18n.exists(`errors:${code}`) ? (code as ErrorKey) : fallback);
}

/** Returns a function that turns any thrown error into a translated, user-facing message. */
export function useErrorMessage() {
  const errorText = useErrorText();
  return (error: unknown): string => errorText(error instanceof ApiError ? error.code : undefined);
}

/**
 * Copies field errors from an API error onto a react-hook-form form.
 * Returns true if at least one field error was applied.
 */
export function applyFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: readonly Path<T>[],
): boolean {
  if (!(error instanceof ApiError)) return false;
  let applied = false;
  for (const [field, code] of Object.entries(error.fields)) {
    if ((fields as readonly string[]).includes(field)) {
      setError(field as Path<T>, { type: "server", message: code });
      applied = true;
    }
  }
  return applied;
}
